import { Module } from 'vuex';

import { RootState } from '@/store';
import { formsService } from '@/services/forms/forms.service';
import { SupplierContact } from '@/services/supplier-contacts/supplier-contacts.types';
import { supplierContactsService } from '@/services/supplier-contacts/supplier-contacts.service';

export interface SupplierContactState {
  supplierContact: SupplierContact;
  supplierContactList: SupplierContact[];
  supplierContactLoading: boolean;
}

const state: SupplierContactState = {
  supplierContact: null,
  supplierContactList: [],
  supplierContactLoading: false
};

const options: Module<SupplierContactState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    getSupplierContact: ({ commit, dispatch }, supplierContactId: number): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .show(supplierContactId)
        .then(supplierContact => supplierContact && commit('supplierContact', supplierContact))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    newSupplierContact: ({ commit, dispatch }): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .new()
        .then(supplierContact => supplierContact && commit('supplierContact', supplierContact))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    createSupplierContact: ({ commit, dispatch }, supplierContact: SupplierContact): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .create(supplierContact)
        .then(supplierContact => supplierContact && commit('supplierContact', supplierContact))
        .then(() => dispatch('alert/pushSuccess', 'Contact fournisseur crée !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    updateSupplierContact: ({ commit, dispatch }, supplierContact: SupplierContact): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .update(supplierContact.id, supplierContact)
        .then(supplierContact => supplierContact && commit('supplierContact', supplierContact))
        .then(() => dispatch('alert/pushSuccess', 'Contact fournisseur mise à jour !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    deleteSupplierContact: ({ commit, dispatch }, supplierContactId: number): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .delete(supplierContactId)
        .then(() => commit('supplierContact', null))
        .then(() => dispatch('alert/pushSuccess', 'Contact fournisseur supprimer !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    getSupplierContactList: ({ commit, dispatch }, supplierId: number): Promise<void> => {
      commit('supplierContactLoading', true);
      return supplierContactsService
        .list(supplierId)
        .then(supplierContactList => supplierContactList && commit('supplierContactList', supplierContactList))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('supplierContactLoading', false));
    },
    clearSupplierContact: ({ commit }): void => {
      commit('supplierContact', null);
    }
  },
  mutations: {
    supplierContact: (state, supplierContact) => (state.supplierContact = supplierContact),
    supplierContactList: (state, supplierContactList) => (state.supplierContactList = supplierContactList),
    supplierContactLoading: (state, loading) => (state.supplierContactLoading = loading)
  },
  getters: {
    supplierContactForm: (state, rootState) =>
      formsService.getForm('supplier-contact', state.supplierContact, rootState['global-settings'])
  }
};

export default options;
